/**
 * features/auth/components/ActiveContextSelector.tsx
 * Muestra el contexto activo (empresa · tienda) en el topbar.
 * Si el usuario tiene permiso `cambiar_tienda_activa`, abre un picker al tocar.
 *
 * P0.1 — Confirmación antes de cambiar tienda si el carrito tiene items.
 * La confirmación es inline (no window.confirm) dentro del mismo sheet.
 */

import { useState } from 'react'
import { useAuth } from '@features/auth/hooks/useAuth'
import type { BusinessOption, StoreOption } from '@features/users/hooks/useUsers'
import { useStorePickerStores } from '@features/auth/hooks/useStorePickerStores'
import { useCartStore } from '@features/pos/store/cartStore'
import { clearDraft } from '@features/pos/store/cartPersistence'
import { useHeldStore } from '@features/pos/store/heldStore'
import { cn } from '@shared/utils/cn'
import { getStoreConfig } from '@features/organization/utils/storeConfig'
import { countPendingForGuard } from '@lib/offline/offlineSaleIntent'
import { OfflinePendingGuardModal } from '@features/pos/components/OfflinePendingGuardModal'

type PendingCtx = { biz: BusinessOption; store: StoreOption }

export function ActiveContextSelector() {
  const { cajero, setActiveContext } = useAuth()
  const { stores: pickerStores } = useStorePickerStores()
  const [open, setOpen] = useState(false)
  const [pendingCtx, setPendingCtx] = useState<PendingCtx | null>(null)
  const [offlineGuard, setOfflineGuard] = useState<{
    count: number
    biz: BusinessOption
    store: StoreOption
  } | null>(null)

  // Detectar estado "sucio" del POS: items, cliente, descuento global o tickets en espera
  // de la tienda activa actual — todo esto se perdería al cambiar contexto.
  const { items, clearCart, clientId, globalDiscount } = useCartStore()
  const { tickets: heldTickets } = useHeldStore()
  const prevActiveStoreId = cajero?.activeStoreId ?? cajero?.storeId ?? ''
  const storeHeldTickets = heldTickets.filter(t => t.storeId === prevActiveStoreId)
  const isDirty =
    items.length > 0 || !!clientId || globalDiscount > 0 || storeHeldTickets.length > 0

  if (!cajero) return null

  // El switcher se habilita si el picker jerárquico devuelve más de una tienda
  // seleccionable (misma fuente que la lista). Antes usaba accessibleStores
  // (user_store_access del login), que subestimaba el alcance de admin/director.
  const canSwitch = pickerStores.length > 1

  const activeStoreCode = cajero.activeStoreCode || cajero.storeCode
  const activeStoreName = cajero.activeStoreName || cajero.storeName

  // Cambio de tienda: fuente jerárquica (RPC get_store_picker_stores).
  // owner → todas · admin/director → tiendas de sus empresas · operativo →
  // solo user_store_access. Igual que el selector inicial.
  const storesForBusiness = (bizId: string): StoreOption[] =>
    pickerStores
      .filter(s => s.business_id === bizId)
      .map(s => ({
        id: s.store_id,
        code: s.store_code,
        name: s.store_name,
        brand: s.business_brand,
        business_id: s.business_id,
      }))
  const visibleBusinesses: BusinessOption[] = Array.from(
    new Map(
      pickerStores.map(s => [
        s.business_id,
        { id: s.business_id, brand: s.business_brand, name: s.business_name },
      ])
    ).values()
  )

  const doSwitch = (biz: BusinessOption, store: StoreOption) => {
    setActiveContext({
      businessId: biz.id,
      businessBrand: biz.brand,
      businessName: biz.name,
      storeId: store.id,
      storeCode: store.code,
      storeName: store.name || store.brand,
    })
    setOpen(false)
    setPendingCtx(null)
  }

  const proceedSelect = (biz: BusinessOption, store: StoreOption) => {
    if (isDirty) {
      setPendingCtx({ biz, store })
      return
    }
    doSwitch(biz, store)
  }

  const handleSelect = async (biz: BusinessOption, store: StoreOption) => {
    // Si ya es la tienda activa, solo cerrar
    if (store.id === cajero.activeStoreId) {
      setOpen(false)
      return
    }
    // P0-1 · Fase 8G — advertir si la tienda ACTUAL tiene intenciones offline
    // pendientes (no se moverán al nuevo contexto ni se reatribuyen).
    const sales = getStoreConfig(cajero.storeConfig ?? {}).sales
    const bid = cajero.activeBusinessId ?? cajero.businessId ?? ''
    if (sales.offline_intents_enabled && cajero.id && cajero.activeStoreId && bid) {
      const { count } = await countPendingForGuard({
        user_id: cajero.id,
        store_id: cajero.activeStoreId,
        business_id: bid,
      })
      if (count > 0) {
        setOfflineGuard({ count, biz, store })
        return
      }
    }
    proceedSelect(biz, store)
  }

  const confirmSwitch = () => {
    if (!pendingCtx || !cajero) return
    // Limpiar draft del localStorage de la tienda anterior ANTES de cambiar contexto.
    // Sin esto, el draft sucio queda guardado y reaparece al volver a esa tienda.
    clearDraft(prevActiveStoreId, cajero.id)
    clearCart()
    doSwitch(pendingCtx.biz, pendingCtx.store)
  }

  const cancelSwitch = () => setPendingCtx(null)

  return (
    <>
      {offlineGuard && (
        <OfflinePendingGuardModal
          count={offlineGuard.count}
          storeLabel={cajero.activeStoreCode ?? cajero.storeCode ?? '—'}
          title="Operaciones pendientes en esta tienda"
          message="Esta tienda tiene operaciones pendientes. No se moverán al nuevo contexto ni se reatribuyen."
          confirmLabel="Cambiar de todas formas"
          onReview={() => {
            setOfflineGuard(null)
            setOpen(false)
            window.location.assign('/pos/pendientes')
          }}
          onConfirm={() => {
            const g = offlineGuard
            setOfflineGuard(null)
            proceedSelect(g.biz, g.store)
          }}
        />
      )}
      <button
        onClick={canSwitch ? () => setOpen(o => !o) : undefined}
        className={cn(
          'flex items-center gap-1.5 leading-none transition-all',
          canSwitch
            ? 'bg-navy-800 hover:bg-navy-600 active:scale-95 text-white text-[11px] font-bold px-2.5 py-1.5 rounded-lg border border-navy-600 cursor-pointer'
            : 'text-surface-400 text-xs cursor-default'
        )}
        title={canSwitch ? 'Cambiar empresa o tienda' : (activeStoreName ?? activeStoreCode)}
      >
        {canSwitch && cajero.activeBusinessBrand && (
          <span className="text-surface-400 font-normal">{cajero.activeBusinessBrand}</span>
        )}
        {canSwitch && cajero.activeBusinessBrand && <span className="text-navy-600">·</span>}
        <span className="font-bold uppercase tracking-wide">{activeStoreCode}</span>
        {canSwitch && <span className="text-brand-400 font-black text-[12px]">⇄</span>}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[400] bg-black/50"
          onClick={() => {
            setOpen(false)
            setPendingCtx(null)
          }}
        >
          <div
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl max-h-[60vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 pt-4 pb-2 border-b border-surface-100">
              <p className="font-bold text-navy-900 text-sm">Cambiar contexto activo</p>
              <button
                onClick={() => {
                  setOpen(false)
                  setPendingCtx(null)
                }}
                className="text-surface-400 hover:text-navy-900 text-lg leading-none"
              >
                ×
              </button>
            </div>

            {/* Confirmación inline cuando hay items en el carrito */}
            {pendingCtx && (
              <div className="px-4 py-3 border-b border-surface-100">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3">
                  <p className="text-xs font-bold text-amber-800 mb-1">
                    ⚠️ Hay una venta en proceso
                  </p>
                  <p className="text-xs text-amber-700">
                    Cambiar a <span className="font-bold">{pendingCtx.store.code}</span> vaciará el
                    carrito, cliente y descuentos de{' '}
                    <span className="font-bold">{activeStoreCode}</span>.
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={cancelSwitch}
                    className="flex-1 py-2 rounded-xl border-2 border-surface-200 text-xs font-semibold text-navy-700 hover:bg-surface-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={confirmSwitch}
                    className="flex-1 py-2 rounded-xl bg-red-500 text-white text-xs font-bold hover:bg-red-600"
                  >
                    Cambiar y vaciar carrito
                  </button>
                </div>
              </div>
            )}

            {/* Store list grouped by business */}
            <div className="p-3 space-y-3">
              {visibleBusinesses.map(biz => {
                const bizStores = storesForBusiness(biz.id)
                if (bizStores.length === 0) return null
                return (
                  <div key={biz.id}>
                    <p className="text-[10px] font-bold text-navy-400 uppercase tracking-wider mb-1 px-1">
                      {biz.brand}
                    </p>
                    <div className="space-y-1">
                      {bizStores.map(store => {
                        const isActive =
                          store.id === cajero.activeStoreId ||
                          (store.code === activeStoreCode && biz.id === cajero.activeBusinessId)
                        return (
                          <button
                            key={store.id}
                            onClick={() => void handleSelect(biz, store)}
                            className={cn(
                              'w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-left transition-colors',
                              isActive
                                ? 'bg-brand-50 text-brand-700 font-semibold'
                                : 'text-navy-700 hover:bg-surface-50'
                            )}
                          >
                            <span className="text-xs font-mono font-bold w-10 flex-shrink-0">
                              {store.code}
                            </span>
                            <span className="text-xs truncate">{store.name || store.brand}</span>
                            {isActive && <span className="ml-auto text-brand-500 text-xs">✓</span>}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )
              })}

              {visibleBusinesses.length === 0 && (
                <p className="text-center text-xs text-surface-400 py-4">
                  No hay tiendas accesibles.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
